import { ReactNode, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/context/auth-context';

/**
 * ProtectedRoute Component
 * 
 * A wrapper intended to secure routes that require an authenticated user.
 * It intercepts rendering based on the `AuthContext`:
 * - If still initializing (`isLoading`), it shows a loading spinner.
 * - If initialization finishes and no valid token/user exists, it force-redirects to `/login`.
 * - Only renders `children` when fully authenticated.
 */
export function ProtectedRoute({ 
  children,
  allowedRoles
}: { 
  children: ReactNode;
  allowedRoles?: string[];
}) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    console.log('[ProtectedRoute] Auth state:', { isLoading, isAuthenticated });
    // Only redirect when NOT loading AND not authenticated AND haven't redirected yet
    if (!isLoading && !isAuthenticated && !hasRedirected.current) {
      console.log('[ProtectedRoute] Redirecting to login');
      hasRedirected.current = true;
      // Clear any stale auth data before redirecting
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      navigate({ to: '/login', replace: true });
    } else if (!isLoading && isAuthenticated && allowedRoles && user?.role) {
      const userRoleLower = user.role.toLowerCase();
      const allowedRolesLower = allowedRoles.map(r => r.toLowerCase());
      
      if (!allowedRolesLower.includes(userRoleLower)) {
        console.log(`[ProtectedRoute] Unauthorized role: ${user.role}. Allowed: ${allowedRoles.join(',')}`);
        navigate({ to: '/', replace: true });
      }
    }
  }, [isLoading, isAuthenticated, user?.role, allowedRoles, navigate]);

  // Show loading spinner while initializing auth
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // During initialization (loading), or when authenticated, render children
  // Only return null when we're sure user is not authenticated (after loading)
  if (!isAuthenticated) {
    return null;
  }

  // If user is authenticated but not authorized, return null to avoid rendering before redirect
  if (allowedRoles && user?.role) {
    const userRoleLower = user.role.toLowerCase();
    const allowedRolesLower = allowedRoles.map(r => r.toLowerCase());
    if (!allowedRolesLower.includes(userRoleLower)) {
      return null;
    }
  }

  return <>{children}</>;
}
